/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import { position } from 'polished';

type Props = {
  children: string,
  disabled?: boolean,
  active?: boolean,
  href: string,
  image: string,
  imagehover: string,
  smalltext: ?string,
};

const defaultProps = {
  disabled: false,
  active: false,
};

export const hubLinkConfig = {
  colors: {
    activeBorder: '#02F082',
    hoverBorder: '#FF8904',
    border: 'black',
    mobileBorder: 'transparent',
  },
};

const A = glamorous(({ disabled, active, href, ...props }) => (
  active || disabled ? (<span {...props} />) : (<a href={href} {...props} />) // eslint-disable-line
))(
  ({ image, theme, imagehover }) => ({
    backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%,rgba(0,0,0,0.4) 100%), url("${image}")`,
    backgroundPosition: 'center center',
    backgroundSize: '100%',
    borderRadius: '6px',
    display: 'block',
    paddingTop: '40%',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    border: `2px solid ${hubLinkConfig.colors.border}`,
    transition: 'border-color .5s ease',
    '&::before': {
      content: '""',
      display: 'block',
      width: '127px',
      height: '140px',
      ...position('absolute', -100, null, null, 0),
      background: `url("${imagehover}") no-repeat`,
      backgroundSize: 'cover',
      opacity: 0,
      transition: 'all .5s ease',
    },
    '&:hover': {
      backgroundImage: `url("${image}")`,
      borderColor: `${hubLinkConfig.colors.hoverBorder}`,
      '::before': {
        opacity: 1,
        ...position('absolute', -35, 0, null, 30),
      },
    },
    [theme.mediaQuery.desktopAndUp]: {
      display: 'flex',
      paddingTop: 0,
    },
    [theme.mediaQuery.tabletLandscapeAndBelow]: {
      borderColor: `${hubLinkConfig.colors.mobileBorder}`,
      '&:hover::before': {
        opacity: 1,
        ...position('absolute', -20, 0, null, 60),
      },
    },
  }),
  ({ active, image }) => (
    active ? ({
      backgroundImage: `url("${image}") !important`,
      borderColor: `${hubLinkConfig.colors.activeBorder} !important`,
      cursor: 'default',
      transition: 'none',
      '&::before': {
        content: 'none',
      },
    }) : {}
  ),
  ({ disabled, theme, image }) => (
    disabled ? ({
      cursor: 'not-allowed',
      transition: 'none',
      '&:hover': {
        borderColor: `${hubLinkConfig.colors.border}`,
        [theme.mediaQuery.tabletLandscapeAndBelow]: {
          borderColor: `${hubLinkConfig.colors.mobileBorder}`,
        },
      },
      [theme.mediaQuery.tabletLandscapeAndBelow]: {
        '&:hover': {
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%,rgba(0,0,0,0.4) 100%), url("${image}")`,
        },
      },
    }) : {}
  ),
);

const Inner = glamorous('div')({
  backgroundImage: 'linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0) 50%)',
  ...position('absolute', 0),
});

const Text = glamorous('div')(({ theme }) => ({
  color: 'white',
  fontSize: '24px',
  fontWeight: 'normal',
  ...position('absolute', null, 0, 0),
  textAlign: 'center',
  textTransform: 'uppercase',
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    ...position('absolute', null, 0, 20),
  },
}));

const SmallText = glamorous('div')({
  fontSize: '1rem',
  lineHeight: '7px',
});

function HubLink({
  children,
  disabled,
  active,
  href,
  image,
  imagehover,
  smalltext,
}: Props) {
  return (
    <A disabled={disabled} active={active} href={href} image={image} imagehover={imagehover} smalltext={smalltext}>
      <Inner>
        <Text>
          {smalltext && <SmallText>{smalltext}</SmallText>}
          {children}
        </Text>
      </Inner>
    </A>
  );
}

HubLink.defaultProps = defaultProps;

export default HubLink;
